import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Plus, Pencil, Trash2, Check, X, Loader2, Home, Briefcase, Star } from "lucide-react";
import { useAppContext } from "../../../context";
import { apiUrl } from "../../../utils/config";
import Footer from "../components/Footer";

const API_BASE = apiUrl;

const ADDRESS_TYPES = ["Home", "Work", "Other"];

const emptyForm = {
  type: "Home",
  fullName: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
};

const typeIcon = (type) => {
  if (type === "Work") return <Briefcase size={14} />;
  if (type === "Home") return <Home size={14} />;
  return <MapPin size={14} />;
};

export default function Addresses() {
  const navigate = useNavigate();
  const { customerUser, updateUserProfile } = useAppContext();

  const [addresses, setAddresses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = new address
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [feedback, setFeedback] = useState({ type: "", text: "" });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (!customerUser) {
      navigate("/login", { replace: true });
      return;
    }
    const stored = customerUser.addresses || [];
    setAddresses(stored);
  }, [customerUser, navigate]);

  const showFeedback = (type, text) => {
    setFeedback({ type, text });
    setTimeout(() => setFeedback({ type: "", text: "" }), 3500);
  };

  const persistAddresses = async (next) => {
    setSaving(true);
    const updated = await updateUserProfile({ addresses: next });
    setSaving(false);
    if (updated) {
      setAddresses(updated.addresses || next);
      return true;
    }
    return false;
  };

  const openNewForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
    setFormOpen(true);
  };

  const openEditForm = (address) => {
    setEditingId(address.id);
    setForm({
      type: address.type || "Home",
      fullName: address.fullName || "",
      line1: address.line1 || "",
      line2: address.line2 || "",
      city: address.city || "",
      state: address.state || "",
      postalCode: address.postalCode || "",
      country: address.country || "",
    });
    setFormError("");
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
  };

  const validateForm = () => {
    if (!form.fullName.trim()) return "Full name is required.";
    if (!form.line1.trim()) return "Street address is required.";
    if (!form.city.trim()) return "City is required.";
    if (!form.country.trim()) return "Country is required.";
    return "";
  };

  const handleSave = async () => {
    const error = validateForm();
    if (error) { setFormError(error); return; }

    setSaving(true);
    let next;

    if (editingId !== null) {
      next = addresses.map((a) =>
        a.id === editingId ? { ...a, ...form } : a
      );
    } else {
      const newAddress = {
        ...form,
        id: `addr_${Date.now()}`,
      };
      next = [...addresses, newAddress];
    }

    const ok = await persistAddresses(next);
    setSaving(false);
    if (ok) {
      showFeedback("success", editingId ? "Address updated." : "Address added.");
      closeForm();
    } else {
      setFormError("Failed to save. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    const next = addresses.filter((a) => a.id !== id);
    setDeletingId(id);
    const ok = await persistAddresses(next);
    setDeletingId(null);
    setConfirmDeleteId(null);
    if (ok) showFeedback("success", "Address removed.");
    else showFeedback("error", "Failed to remove address.");
  };

  const handleSetDefault = async (id) => {
    const target = addresses.find((a) => a.id === id);
    if (!target) return;
    const rest = addresses.filter((a) => a.id !== id);
    const next = [target, ...rest];
    const ok = await persistAddresses(next);
    if (ok) showFeedback("success", "Default address updated.");
    else showFeedback("error", "Failed to update default.");
  };

  const inputCls =
    "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 transition";

  if (!customerUser) return null;

  return (
    <>
      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate("/profile")}
              className="text-sm text-gray-400 hover:text-gray-600 mb-1 flex items-center gap-1 transition"
              type="button"
            >
              ← Back to Profile
            </button>
            <h1 className="text-2xl font-serif font-bold text-gray-900 flex items-center gap-2">
              <MapPin className="text-[#f97316]" size={22} />
              Saved Addresses
            </h1>
          </div>
          <button
            onClick={openNewForm}
            className="flex items-center gap-2 bg-[#f97316] hover:bg-orange-600 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition"
            type="button"
          >
            <Plus size={16} />
            Add Address
          </button>
        </div>

        {/* Feedback banner */}
        {feedback.text && (
          <div
            className={`mb-4 rounded-2xl border px-4 py-3 text-sm font-medium ${
              feedback.type === "error"
                ? "border-red-100 bg-red-50 text-red-600"
                : "border-green-100 bg-green-50 text-green-600"
            }`}
          >
            {feedback.text}
          </div>
        )}

        {/* Add / Edit form (inline card) */}
        {formOpen && (
          <div className="bg-white rounded-3xl border-2 border-[#f97316] shadow-lg shadow-orange-100 p-6 mb-6 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-gray-900 text-base">
                {editingId ? "Edit Address" : "New Address"}
              </h2>
              <button onClick={closeForm} type="button" className="p-1.5 hover:bg-gray-100 rounded-full transition">
                <X size={16} className="text-gray-400" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Type selector */}
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1.5">Address Type</label>
                <div className="flex gap-2">
                  {ADDRESS_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, type: t }))}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition ${
                        form.type === t
                          ? "bg-orange-50 border-orange-300 text-[#f97316]"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      {typeIcon(t)} {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Full name */}
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1.5">Full Name *</label>
                <input
                  value={form.fullName}
                  onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                  placeholder="e.g. John Smith"
                  className={inputCls}
                />
              </div>

              {/* Street */}
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1.5">Street Address *</label>
                <input
                  value={form.line1}
                  onChange={(e) => setForm((f) => ({ ...f, line1: e.target.value }))}
                  placeholder="123 Main Street"
                  className={inputCls}
                />
              </div>

              {/* Apt / Suite */}
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1.5">Apt / Suite / Floor</label>
                <input
                  value={form.line2}
                  onChange={(e) => setForm((f) => ({ ...f, line2: e.target.value }))}
                  placeholder="Apt 4B (optional)"
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1.5">City *</label>
                  <input
                    value={form.city}
                    onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                    placeholder="New York"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1.5">State / Province</label>
                  <input
                    value={form.state}
                    onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                    placeholder="NY"
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1.5">Postal Code</label>
                  <input
                    value={form.postalCode}
                    onChange={(e) => setForm((f) => ({ ...f, postalCode: e.target.value }))}
                    placeholder="10001"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1.5">Country *</label>
                  <input
                    value={form.country}
                    onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                    placeholder="United States"
                    className={inputCls}
                  />
                </div>
              </div>

              {formError && (
                <p className="text-xs text-red-500 font-medium">{formError}</p>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  type="button"
                  className="flex items-center gap-2 bg-[#f97316] hover:bg-orange-600 text-white font-bold px-6 py-2.5 rounded-xl transition disabled:opacity-60"
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                  {editingId ? "Update" : "Save Address"}
                </button>
                <button
                  onClick={closeForm}
                  type="button"
                  className="px-5 py-2.5 text-sm font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-xl transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Address list */}
        {addresses.length === 0 && !formOpen ? (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-12 text-center">
            <MapPin size={36} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium mb-1">No saved addresses yet</p>
            <p className="text-sm text-gray-400 mb-5">Add your first delivery address to speed up checkout.</p>
            <button
              onClick={openNewForm}
              type="button"
              className="inline-flex items-center gap-2 bg-[#f97316] hover:bg-orange-600 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition"
            >
              <Plus size={15} /> Add Address
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {addresses.map((address, index) => (
              <div
                key={address.id || index}
                className={`bg-white rounded-2xl p-5 shadow-sm relative transition ${
                  index === 0
                    ? "border-2 border-[#f97316]"
                    : "border border-gray-100 hover:border-gray-200"
                }`}
              >
                {/* Default badge */}
                {index === 0 && (
                  <span className="absolute top-4 right-4 flex items-center gap-1 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    <Star size={9} /> Default
                  </span>
                )}

                <div className="flex items-start gap-3">
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      index === 0
                        ? "bg-orange-100 text-[#f97316]"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {typeIcon(address.type)}
                  </div>
                  <div className="flex-1 min-w-0 pr-16">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-bold text-gray-900 text-sm">{address.type || "Address"}</span>
                    </div>
                    <p className="font-semibold text-gray-800 text-sm">{address.fullName}</p>
                    <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">
                      {[address.line1, address.line2].filter(Boolean).join(", ")}
                      {address.city || address.state || address.postalCode
                        ? `, ${[address.city, address.state, address.postalCode].filter(Boolean).join(" ")}`
                        : ""}
                      {address.country ? `, ${address.country}` : ""}
                    </p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-50">
                  <button
                    type="button"
                    onClick={() => openEditForm(address)}
                    className="flex items-center gap-1.5 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition"
                  >
                    <Pencil size={12} /> Edit
                  </button>

                  {index !== 0 && (
                    <button
                      type="button"
                      onClick={() => handleSetDefault(address.id)}
                      disabled={saving}
                      className="flex items-center gap-1.5 text-xs font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-1.5 rounded-lg transition disabled:opacity-50"
                    >
                      <Star size={12} /> Set as Default
                    </button>
                  )}

                  {confirmDeleteId === address.id ? (
                    <div className="flex items-center gap-2 ml-auto">
                      <span className="text-xs text-gray-500">Remove this address?</span>
                      <button
                        type="button"
                        onClick={() => handleDelete(address.id)}
                        disabled={deletingId === address.id}
                        className="flex items-center gap-1 text-xs font-bold text-white bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded-lg transition"
                      >
                        {deletingId === address.id ? (
                          <Loader2 size={11} className="animate-spin" />
                        ) : (
                          <Trash2 size={11} />
                        )}
                        Yes, remove
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-xs font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(address.id)}
                      className="flex items-center gap-1.5 text-xs font-bold text-red-400 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition ml-auto"
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Add another */}
            {!formOpen && (
              <button
                type="button"
                onClick={openNewForm}
                className="w-full border-2 border-dashed border-gray-200 rounded-2xl py-6 text-gray-400 hover:border-orange-300 hover:text-orange-400 transition font-semibold text-sm flex items-center justify-center gap-2"
              >
                <Plus size={16} /> Add Another Address
              </button>
            )}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
